import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost } from './api';

function clsx(...xs) { return xs.filter(Boolean).join(' '); }

function Badge({ children, tone = 'slate' }) {
  const map = {
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
    green: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    red: 'bg-rose-100 text-rose-700 border-rose-200',
    amber: 'bg-amber-100 text-amber-800 border-amber-200',
    blue: 'bg-sky-100 text-sky-700 border-sky-200',
    purple: 'bg-purple-100 text-purple-700 border-purple-200',
  };
  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-[11px] border', map[tone] || map.slate)}>
      {children}
    </span>
  );
}

function Button({ children, onClick, variant = 'primary', disabled = false, className = '', type='button' }) {
  const base = 'px-4 py-2 rounded-xl text-sm font-semibold transition-colors inline-flex items-center justify-center';
  const styles =
    variant === 'primary'
      ? 'bg-sky-700 text-white hover:bg-sky-800 disabled:opacity-60'
      : variant === 'danger'
      ? 'bg-rose-700 text-white hover:bg-rose-800 disabled:opacity-60'
      : 'bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 disabled:opacity-60';
  return (
    <button type={type} disabled={disabled} onClick={onClick} className={clsx(base, styles, className)}>
      {children}
    </button>
  );
}

function Modal({ open, title, children, onClose, wide=false }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[250] bg-black/40 flex items-center justify-center p-3">
      <div className={clsx('bg-white w-full rounded-2xl shadow-xl border', wide ? 'max-w-3xl' : 'max-w-xl')}>
        <div className="p-4 border-b flex items-center justify-between gap-3">
          <div className="font-semibold text-slate-900">{title}</div>
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

function pad2(n) { return String(n).padStart(2, '0'); }
function formatDTShort(dtStr) {
  if (!dtStr) return '—';
  const d = new Date(dtStr);
  if (Number.isNaN(d.getTime())) return String(dtStr).replace('T', ' ').slice(0, 16);
  const y = d.getFullYear();
  const m = pad2(d.getMonth() + 1);
  const day = pad2(d.getDate());
  const hh = pad2(d.getHours());
  const mm = pad2(d.getMinutes());
  return `${y}-${m}-${day} ${hh}:${mm}`;
}

function statusTone(status) {
  const s = String(status || 'draft');
  if (s === 'locked') return 'purple';
  if (s === 'submitted') return 'green';
  return 'amber';
}

const DISCIPLINE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'PROG_EI', label: 'PROG_EI' },
  { value: 'PROG_PI', label: 'PROG_PI' },
  { value: 'PROG_ABA', label: 'PROG_ABA' },
  { value: 'PROG_OT', label: 'PROG_OT' },
  { value: 'PROG_PT', label: 'PROG_PT' },
  { value: 'PROG_ST', label: 'PROG_ST' },
];

function getWorkspaceBasePrefix() {
  try {
    const parts = window.location.pathname.split('/').filter(Boolean);
    const first = parts[0] || '';
    const allowed = new Set(['admin', 'super', 'parent', 'teacher', 'app', 'dashboard']);
    if (allowed.has(first)) return `/${first}`;
    return '';
  } catch {
    return '';
  }
}

export default function AdminInstrumentsAvailability() {
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState(''); // ✅ start empty (admin must choose)

  const [discipline, setDiscipline] = useState('all');
  const [formKind, setFormKind] = useState('all');
  const [activeOnly, setActiveOnly] = useState(true);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');

  // existing submissions
  const [existingLoading, setExistingLoading] = useState(false);
  const [existing, setExisting] = useState([]);
  const [existingPeriod, setExistingPeriod] = useState('all');
  const [existingStatus, setExistingStatus] = useState('all');

  // confirm modal for Start
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [pendingStart, setPendingStart] = useState(null);
  // pendingStart = { instrumentId, period, instrument_code, instrument_name, version }

  const workspacePrefix = useMemo(() => getWorkspaceBasePrefix(), []);

  const selectedStudent = useMemo(() => {
    if (!studentId) return null;
    return students.find((s) => String(s.id) === String(studentId)) || null;
  }, [students, studentId]);

  function studentLabel(st) {
    if (!st) return '—';
    const ln = (st.last_name || '').trim();
    const fn = (st.first_name || '').trim();
    const pt = (st.program_type || '').trim();
    const name = `${ln}${ln && fn ? ', ' : ''}${fn}`.trim() || 'Unnamed';
    return pt ? `${name} (${pt})` : name;
  }

  async function loadStudents() {
    try {
      const data = await apiGet('/me/students');
      const arr = Array.isArray(data) ? data : (data.students || []);
      setStudents(arr);
      // ✅ do NOT auto-select first student
    } catch (e) {
      console.error(e);
      setStudents([]);
    }
  }

  async function loadAvailable() {
    setLoading(true);
    try {
      const params = {};
      params.active = activeOnly ? 1 : 0;
      if (formKind !== 'all') params.form_kind = formKind;
      if (discipline !== 'all') params.discipline_code = discipline;

      let data = null;
      try {
        data = await apiGet('/instruments/available', params);
      } catch {
        data = await apiGet('/instruments/available', { params });
      }

      const usableOnly = (data.items || []).filter((x) => x.can_use);
      setItems(usableOnly);
    } catch (e) {
      console.error(e);
      alert('Failed to load available instruments');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  async function loadExistingSubmissions(sid = studentId) {
    if (!sid) { setExisting([]); return; }
    setExistingLoading(true);
    try {
      const params = { status: existingStatus };
      if (existingPeriod !== 'all') params.period = existingPeriod;

      const res = await apiGet(`/students/${Number(sid)}/instrument-submissions`, params);
      setExisting(res.submissions || []);
    } catch (e) {
      console.error(e);
      setExisting([]);
    } finally {
      setExistingLoading(false);
    }
  }

  useEffect(() => { loadStudents(); }, []); // eslint-disable-line
  useEffect(() => { loadAvailable(); }, [discipline, formKind, activeOnly]); // eslint-disable-line
  useEffect(() => { loadExistingSubmissions(studentId); }, [studentId, existingPeriod, existingStatus]); // eslint-disable-line

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (items || []).filter((it) => {
      if (!t) return true;
      return (
        String(it.instrument_code || '').toLowerCase().includes(t) ||
        String(it.name || '').toLowerCase().includes(t) ||
        String(it.discipline_code || '').toLowerCase().includes(t)
      );
    });
  }, [items, q]);

  const existingByKey = useMemo(() => {
    const m = new Map();
    for (const s of existing || []) {
      const k = `${s.instrument_id}|${s.period}`;
      const prev = m.get(k);
      if (!prev) m.set(k, s);
      else {
        const tPrev = new Date(prev.started_at || prev.updated_at || 0).getTime();
        const tNow = new Date(s.started_at || s.updated_at || 0).getTime();
        if (tNow >= tPrev) m.set(k, s);
      }
    }
    return m;
  }, [existing]);

  function openStartConfirm(it, period) {
    if (!studentId) {
      alert('Please select a student first.');
      return;
    }
    setPendingStart({
      instrumentId: Number(it.id),
      period,
      instrument_code: it.instrument_code,
      instrument_name: it.name,
      version: it.version,
    });
    setConfirmOpen(true);
  }

  async function doStartSubmission() {
    if (!pendingStart) return;
    if (!studentId) {
      setConfirmOpen(false);
      setPendingStart(null);
      alert('Please select a student first.');
      return;
    }

    setConfirmBusy(true);
    try {
      const data = await apiPost('/instrument-submissions/start', {
        student_id: Number(studentId),
        instrument_id: Number(pendingStart.instrumentId),
        period: String(pendingStart.period),
      });

      const sid = data?.submission_id;
      if (!sid) {
        alert('Started, but missing submission_id in response');
        return;
      }

      await loadExistingSubmissions(studentId);

      setConfirmOpen(false);
      setPendingStart(null);

      window.location.href = `${workspacePrefix}/instrument-submissions/${sid}`;
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to start submission');
    } finally {
      setConfirmBusy(false);
    }
  }

  function openSubmission(subId) {
    const idNum = Number(subId);
    if (!idNum || !Number.isFinite(idNum)) {
      alert('Invalid submission id.');
      return;
    }
    window.location.href = `${workspacePrefix}/instrument-submissions/${idNum}`;
  }

  const confirmExisting = useMemo(() => {
    if (!pendingStart) return null;
    const k = `${pendingStart.instrumentId}|${pendingStart.period}`;
    return existingByKey.get(k) || null;
  }, [pendingStart, existingByKey]);

  return (
    <div className="p-4 max-w-7xl mx-auto">
      {/* Neutral header */}
      <div className="bg-white border rounded-2xl shadow-sm p-4">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xl font-semibold text-slate-900 truncate">
              Instruments & Intakes
            </div>
            <div className="mt-1 text-sm text-slate-600">
              Shows what your organization can use and what’s already assigned to the selected student.
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 justify-start lg:justify-end">
            <Button variant="secondary" onClick={loadAvailable} disabled={loading}>
              {loading ? 'Loading…' : 'Reload catalog'}
            </Button>
            <Button variant="secondary" onClick={() => loadExistingSubmissions(studentId)} disabled={existingLoading || !studentId}>
              {existingLoading ? 'Loading…' : 'Reload student list'}
            </Button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-2">
          <div className="lg:col-span-5">
            <label className="text-xs text-slate-600">Student</label>
            <select
              className="w-full px-3 py-2 rounded-xl border bg-white text-slate-900"
              value={studentId}
              onChange={(e) => setStudentId(e.target.value)}
            >
              <option value="">Select student…</option>
              {students.map((s) => (
                <option key={s.id} value={s.id}>
                  {studentLabel(s)}
                </option>
              ))}
            </select>
          </div>

          <div className="lg:col-span-2">
            <label className="text-xs text-slate-600">Discipline</label>
            <select
              className="w-full px-3 py-2 rounded-xl border bg-white text-slate-900"
              value={discipline}
              onChange={(e) => setDiscipline(e.target.value)}
            >
              {DISCIPLINE_OPTIONS.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </div>

          <div className="lg:col-span-2">
            <label className="text-xs text-slate-600">Form kind</label>
            <select
              className="w-full px-3 py-2 rounded-xl border bg-white text-slate-900"
              value={formKind}
              onChange={(e) => setFormKind(e.target.value)}
            >
              <option value="all">all</option>
              <option value="instrument">instrument</option>
              <option value="intake">intake</option>
            </select>
          </div>

          <div className="lg:col-span-1 flex items-end">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
              />
              Active
            </label>
          </div>

          <div className="lg:col-span-2">
            <label className="text-xs text-slate-600">Search catalog</label>
            <input
              className="w-full px-3 py-2 rounded-xl border bg-white text-slate-900"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="code or name…"
            />
          </div>
        </div>
      </div>

      {/* Existing submissions list */}
      <div className="mt-4 bg-white border rounded-2xl shadow-sm overflow-hidden">
        <div className="p-3 border-b flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <div>
            <div className="font-semibold text-slate-900">Existing submissions for selected student</div>
            <div className="text-xs text-slate-500">
              Helps avoid accidental duplicates.
            </div>
          </div>

          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <label className="text-[11px] text-slate-600">Period</label>
              <select
                className="px-3 py-2 rounded-xl border bg-white text-slate-900 text-sm"
                value={existingPeriod}
                onChange={(e) => setExistingPeriod(e.target.value)}
              >
                <option value="all">all</option>
                <option value="intake">intake</option>
                <option value="exit">exit</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] text-slate-600">Status</label>
              <select
                className="px-3 py-2 rounded-xl border bg-white text-slate-900 text-sm"
                value={existingStatus}
                onChange={(e) => setExistingStatus(e.target.value)}
              >
                <option value="all">all</option>
                <option value="draft">draft</option>
                <option value="submitted">submitted</option>
                <option value="locked">locked</option>
              </select>
            </div>
            <Button variant="secondary" onClick={() => loadExistingSubmissions(studentId)} disabled={!studentId || existingLoading}>
              {existingLoading ? 'Loading…' : 'Refresh'}
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[1100px] w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr className="text-left">
                <th className="p-3">Template</th>
                <th className="p-3">Kind</th>
                <th className="p-3">Period</th>
                <th className="p-3">Status</th>
                <th className="p-3">Started</th>
                <th className="p-3">Submitted</th>
                <th className="p-3 text-right">Open</th>
              </tr>
            </thead>
            <tbody>
              {!studentId ? (
                <tr><td colSpan={7} className="p-6 text-center text-slate-500">Select a student to view submissions.</td></tr>
              ) : existingLoading ? (
                <tr><td colSpan={7} className="p-6 text-center text-slate-500">Loading…</td></tr>
              ) : (existing || []).length ? (
                (existing || []).map((s) => (
                  <tr key={s.id} className="border-b hover:bg-slate-50">
                    <td className="p-3">
                      <div className="font-mono text-[12px] text-slate-700">
                        {s.instrument_code} <span className="text-slate-500">v{s.version}</span>
                      </div>
                      <div className="font-medium text-slate-900">{s.instrument_name}</div>
                    </td>
                    <td className="p-3"><Badge tone={s.form_kind === 'intake' ? 'purple' : 'blue'}>{s.form_kind}</Badge></td>
                    <td className="p-3"><Badge tone="amber">{s.period}</Badge></td>
                    <td className="p-3"><Badge tone={statusTone(s.status)}>{s.status}</Badge></td>
                    <td className="p-3">{formatDTShort(s.started_at)}</td>
                    <td className="p-3">{formatDTShort(s.submitted_at)}</td>
                    <td className="p-3 text-right">
                      <Button variant="secondary" onClick={() => openSubmission(s.id)}>Open</Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={7} className="p-6 text-center text-slate-500">No submissions found for current filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Catalog */}
      <div className="mt-4 bg-white border rounded-2xl shadow-sm overflow-hidden">
        <div className="p-3 border-b flex items-center justify-between">
          <div className="text-sm text-slate-600">{loading ? 'Loading…' : `${filtered.length} catalog items`}</div>
          <Button variant="secondary" onClick={loadAvailable} disabled={loading}>Refresh</Button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[1200px] w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr className="text-left">
                <th className="p-3">Code</th>
                <th className="p-3">Name</th>
                <th className="p-3">Scope</th>
                <th className="p-3">Kind</th>
                <th className="p-3">Discipline</th>
                <th className="p-3">Distribution</th>
                <th className="p-3">Can use</th>
                <th className="p-3">Already for student?</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => {
                const exIntake = existingByKey.get(`${it.id}|intake`);
                const exExit = existingByKey.get(`${it.id}|exit`);

                return (
                  <tr key={it.id} className="border-b hover:bg-slate-50">
                    <td className="p-3 font-mono text-[12px]">{it.instrument_code} <span className="text-slate-500">v{it.version}</span></td>
                    <td className="p-3">
                      <div className="font-medium text-slate-900">{it.name}</div>
                      {it.description ? <div className="text-xs text-slate-500 line-clamp-1">{it.description}</div> : null}
                    </td>
                    <td className="p-3">{it.organization_id ? <Badge tone="blue">ORG</Badge> : <Badge tone="slate">GLOBAL</Badge>}</td>
                    <td className="p-3"><Badge tone={it.form_kind === 'intake' ? 'purple' : 'blue'}>{it.form_kind}</Badge></td>
                    <td className="p-3">{it.discipline_code ? <Badge>{it.discipline_code}</Badge> : <span className="text-slate-400">—</span>}</td>
                    <td className="p-3"><Badge tone={it.distribution_mode === 'shell_only' ? 'slate' : 'green'}>{it.distribution_mode}</Badge></td>
                    <td className="p-3">
                      {it.can_use ? <Badge tone="green">YES</Badge> : (
                        <div className="space-y-1">
                          <Badge tone="red">NO</Badge>
                          <div className="text-xs text-slate-500 max-w-[320px]">{it.cannot_reason || 'Not licensed'}</div>
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      {!studentId ? (
                        <span className="text-xs text-slate-400">Select student</span>
                      ) : (
                        <div className="space-y-1">
                          <div className="text-xs">
                            <span className="text-slate-500">intake:</span>{' '}
                            {exIntake ? <Badge tone={statusTone(exIntake.status)}>{exIntake.status}</Badge> : <span className="text-slate-400">—</span>}
                          </div>
                          <div className="text-xs">
                            <span className="text-slate-500">exit:</span>{' '}
                            {exExit ? <Badge tone={statusTone(exExit.status)}>{exExit.status}</Badge> : <span className="text-slate-400">—</span>}
                          </div>
                        </div>
                      )}
                    </td>

                    <td className="p-3 text-right">
                      <div className="flex flex-wrap gap-2 justify-end min-w-[220px]">
                        <Button
                          variant="primary"
                          disabled={!it.can_use || !studentId}
                          onClick={() => openStartConfirm(it, 'intake')}
                          className="px-3 py-1.5 rounded-lg"
                        >
                          Start intake
                        </Button>
                        <Button
                          variant="secondary"
                          disabled={!it.can_use || !studentId}
                          onClick={() => openStartConfirm(it, 'exit')}
                          className="px-3 py-1.5 rounded-lg"
                        >
                          Start exit
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!filtered.length && !loading ? (
                <tr><td colSpan={9} className="p-6 text-center text-slate-500">No items.</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {/* Confirm Start modal */}
      <Modal
        open={confirmOpen}
        title={pendingStart ? `Confirm: Start ${pendingStart.period}` : 'Confirm'}
        onClose={() => { if (!confirmBusy) { setConfirmOpen(false); setPendingStart(null); } }}
      >
        {!pendingStart ? (
          <div className="text-sm text-slate-600">—</div>
        ) : (
          <div className="space-y-3">
            <div className="text-sm text-slate-700">
              You are about to <span className="font-semibold">start/resume</span> a <Badge tone="amber">{pendingStart.period}</Badge> submission for:
            </div>

            <div className="border rounded-2xl p-3 bg-slate-50">
              <div className="text-xs text-slate-500">Student</div>
              <div className="font-semibold text-slate-900">{studentLabel(selectedStudent)}</div>
            </div>

            <div className="border rounded-2xl p-3 bg-slate-50">
              <div className="text-xs text-slate-500">Template</div>
              <div className="font-mono text-[12px] text-slate-700">
                {pendingStart.instrument_code} <span className="text-slate-500">v{pendingStart.version}</span>
              </div>
              <div className="font-semibold text-slate-900">{pendingStart.instrument_name}</div>
            </div>

            {confirmExisting ? (
              <div className="border rounded-2xl p-3 bg-amber-50 border-amber-200">
                <div className="text-sm text-amber-900 font-semibold">Already exists for this student</div>
                <div className="mt-1 text-sm text-amber-900">
                  Status: <Badge tone={statusTone(confirmExisting.status)}>{confirmExisting.status}</Badge>
                  <span className="ml-2 text-amber-800">
                    Started: {formatDTShort(confirmExisting.started_at)}
                  </span>
                </div>
                <div className="mt-2 text-xs text-amber-800">
                  Clicking “Confirm” will typically <b>resume the existing draft</b> (not create duplicates), but please verify.
                </div>
              </div>
            ) : (
              <div className="border rounded-2xl p-3 bg-emerald-50 border-emerald-200">
                <div className="text-sm text-emerald-900 font-semibold">No existing submission found</div>
                <div className="text-xs text-emerald-800 mt-1">
                  This will create a new draft submission for this student.
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="secondary"
                disabled={confirmBusy}
                onClick={() => { setConfirmOpen(false); setPendingStart(null); }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={confirmBusy}
                onClick={doStartSubmission}
              >
                {confirmBusy ? 'Starting…' : 'Confirm'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}




